import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Smartphone,
  Laptop,
  Key,
  Watch,
  Headphones,
  Camera,
  Package,
  User,
  Mail,
  Phone,
  AlertCircle,
  CheckCircle,
  Home,
  Save,
  ArrowLeft,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import "../styles/AssignPage.css";

// Import Firebase
import {
  doc,
  getDocs,
  updateDoc,
  serverTimestamp,
  collection,
  addDoc,
} from "firebase/firestore";
import { db } from "../lib/firebase";

// Types d'objets disponibles
const OBJECT_TYPES = [
  {
    id: "Téléphone",
    label: "Téléphone",
    icon: Smartphone,
    description: "Smartphone ou téléphone mobile",
  },
  {
    id: "laptop",
    label: "Ordinateur portable",
    icon: Laptop,
    description: "Ordinateur portable ou tablette",
  },
  {
    id: "keys",
    label: "Clés",
    icon: Key,
    description: "Clés de maison, voiture ou bureau",
  },
  {
    id: "wallet",
    label: "Portefeuille",
    icon: Package,
    description: "Portefeuille ou porte-cartes",
  },
  {
    id: "watch",
    label: "Montre",
    icon: Watch,
    description: "Montre connectée ou traditionnelle",
  },
  {
    id: "headphones",
    label: "Écouteurs",
    icon: Headphones,
    description: "Écouteurs ou casque audio",
  },
  {
    id: "camera",
    label: "Appareil photo",
    icon: Camera,
    description: "Appareil photo ou caméra",
  },
  {
    id: "bag",
    label: "Sac",
    icon: Package,
    description: "Sac à main, sac à dos ou valise",
  },
  {
    id: "other",
    label: "Autre",
    icon: Package,
    description: "Autre type d'objet",
  },
];

export default function AssignPage() {
  const { qrId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [error, setError] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    objectType: "",
    objectName: "",
    description: "",
    ownerName: "",
    ownerEmail: "",
    ownerPhone: "",
    ownerPhone2: "", // Second numéro
    color: "",
  });

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    loadQRCode();
  }, [qrId]);

  const loadQRCode = async () => {
    setLoading(true);
    try {
      // Chercher le QR code par qrId ou id
      const qrCodesRef = collection(db, "qrCodes");
      const querySnapshot = await getDocs(qrCodesRef);

      const foundQr = querySnapshot.docs.find(
        (doc) => doc.data().qrId === qrId || doc.id === qrId
      );

      if (!foundQr) {
        setError("QR code non trouvé");
        setLoading(false);
        return;
      }

      const qrData = foundQr.data();

      // REDIRECTION AJOUTÉE: Si déjà assigné, rediriger vers la page de scan
      if (qrData.status === "assigned") {
        // Rediriger vers la page de scan au lieu d'afficher une erreur
        navigate(`/scan/${qrId}`);
        return;
      }

      setQrCode({
        id: foundQr.id,
        ...qrData,
      });
    } catch (error) {
      console.error("Erreur chargement QR code:", error);
      setError("Erreur de chargement du QR code");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.objectType) {
      errors.objectType = "Veuillez sélectionner un type d'objet";
    }

    if (!formData.objectName.trim()) {
      errors.objectName = "Veuillez donner un nom à votre objet";
    }

    if (!formData.ownerName.trim()) {
      errors.ownerName = "Veuillez entrer votre nom";
    }

    // Validation email - maintenant facultatif
    const email = formData.ownerEmail.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.ownerEmail = "Email invalide";
    }

    // Validation des numéros - au moins un est requis
    const phone1 = formData.ownerPhone.trim();
    const phone2 = formData.ownerPhone2.trim();

    if (!phone1 && !phone2) {
      errors.ownerPhone = "Veuillez entrer au moins un numéro de téléphone";
    }

    // Validation du format des numéros si remplis
    if (phone1 && !/^[\+]?[0-9\s\-\(\)\.]{10,20}$/.test(phone1)) {
      errors.ownerPhone = "Format de téléphone invalide";
    }

    if (phone2 && !/^[\+]?[0-9\s\-\(\)\.]{10,20}$/.test(phone2)) {
      errors.ownerPhone2 = "Format de téléphone invalide";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Veuillez corriger les erreurs dans le formulaire");
      return;
    }

    if (!qrCode) {
      toast.error("QR code non disponible");
      return;
    }

    setSubmitting(true);

    try {
      const qrRef = doc(db, "qrCodes", qrCode.id);

      // Préparer les données de mise à jour
      const updateData = {
        status: "assigned",
        objectType: formData.objectType,
        objectName: formData.objectName,
        description: formData.description || "",
        color: formData.color || "",

        // Informations propriétaire
        ownerName: formData.ownerName,
        ownerEmail: formData.ownerEmail || null,
        ownerPhone: formData.ownerPhone,
        ownerPhone2: formData.ownerPhone2 || null, // Sauvegarder le 2ème numéro

        // Métadonnées
        firstScan: serverTimestamp(),
        lastScan: serverTimestamp(),
        scanCount: 1,
        updatedAt: serverTimestamp(),
        assignedAt: serverTimestamp(),
      };

      console.log("Données à sauvegarder:", updateData); // Pour déboguer

      // Mettre à jour le QR code
      await updateDoc(qrRef, updateData);

      // Créer un enregistrement de scan
      await addDoc(collection(db, "scans"), {
        qrCodeId: qrCode.id,
        qrId: qrCode.qrId,
        type: "assignment",
        timestamp: serverTimestamp(),
        userEmail: formData.ownerEmail,
        userPhone: formData.ownerPhone,
        userPhone2: formData.ownerPhone2, // Ajouter le 2ème numéro
        objectName: formData.objectName,
        objectType: formData.objectType,
        metadata: {
          ip: await getClientIP(),
          userAgent: navigator.userAgent,
        },
      });

      // Créer un objet dans la collection objects
      await addDoc(collection(db, "objects"), {
        qrCodeId: qrCode.id,
        qrId: qrCode.qrId,
        name: formData.objectName,
        type: formData.objectType,
        description: formData.description,
        color: formData.color,
        ownerName: formData.ownerName,
        ownerEmail: formData.ownerEmail,
        ownerPhone: formData.ownerPhone,
        ownerPhone2: formData.ownerPhone2, // Ajouter le 2ème numéro
        status: "active",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastSeen: serverTimestamp(),
        isFound: false,
      });

      toast.success("Objet assigné avec succès !");

      // Rediriger vers la page de confirmation
      navigate(`/assign/success/${qrCode.qrId}`);
    } catch (error) {
      console.error("Erreur assignation:", error);
      toast.error("Erreur lors de l'assignation");
    } finally {
      setSubmitting(false);
    }
  };

  const getClientIP = async () => {
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      return data.ip;
    } catch (error) {
      return "unknown";
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Effacer l'erreur correspondante quand l'utilisateur commence à taper
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    // Gestion spéciale pour la validation "au moins un numéro"
    if (name === "ownerPhone" || name === "ownerPhone2") {
      const phone1 = name === "ownerPhone" ? value : formData.ownerPhone;
      const phone2 = name === "ownerPhone2" ? value : formData.ownerPhone2;

      // Si l'erreur "au moins un numéro" était présente et qu'un numéro est maintenant rempli
      if (
        formErrors.ownerPhone ===
        "Veuillez entrer au moins un numéro de téléphone"
      ) {
        if (phone1.trim() || phone2.trim()) {
          setFormErrors((prev) => ({
            ...prev,
            ownerPhone: "",
          }));
        }
      }
    }
  };

  const handleTypeSelect = (typeId) => {
    setFormData((prev) => ({
      ...prev,
      objectType: typeId,
    }));

    if (formErrors.objectType) {
      setFormErrors((prev) => ({
        ...prev,
        objectType: "",
      }));
    }
  };

  if (loading) {
    return (
      <div className="assign-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p className="loading-text">Chargement du QR code...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="assign-page">
        <div className="error-container">
          <AlertCircle size={48} className="error-icon" />
          <h2 className="error-title">QR Code non disponible</h2>
          <p className="error-message">{error}</p>
          <button onClick={() => navigate("/")} className="btn btn-primary">
            <Home size={16} />
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="assign-page">
      <header className="assign-header">
        <div className="container">
          <div className="header-content">
            <button onClick={() => navigate("/")} className="back-btn">
              <ArrowLeft size={20} />
              Retour
            </button>
            <div className="header-brand">
              <h1>UltimaTag</h1>
              <p className="header-subtitle">Assigner votre objet</p>
            </div>
            <div className="qr-info">
              <span className="qr-label">QR Code:</span>
              <code className="qr-id">{qrCode?.qrId}</code>
            </div>
          </div>
        </div>
      </header>

      <main className="assign-main">
        <div className="container">
          <div className="assign-content">
            <div className="assign-card">
              <div className="assign-intro">
                <CheckCircle size={48} className="intro-icon" />
                <h2 className="intro-title">Protégez votre objet</h2>
                <p className="intro-text">
                  Assignez ce QR code à votre objet pour le protéger en cas de
                  perte. Si quelqu'un le trouve et scanne le code, vous serez
                  immédiatement notifié.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="assign-form">
                {/* Étape 1: Type d'objet */}
                <div className="form-section">
                  <h3 className="section-title">
                    <span className="section-number">1</span>
                    Quel type d'objet possédez-vous ?
                  </h3>
                  <p className="section-description">
                    Sélectionnez la catégorie qui correspond le mieux à votre
                    objet
                  </p>

                  <div className="object-types-grid">
                    {OBJECT_TYPES.map((type) => {
                      const Icon = type.icon;
                      const isSelected = formData.objectType === type.id;

                      return (
                        <button
                          key={type.id}
                          type="button"
                          className={`object-type-card ${
                            isSelected ? "selected" : ""
                          }`}
                          onClick={() => handleTypeSelect(type.id)}
                        >
                          <div className="type-icon">
                            <Icon size={24} />
                          </div>
                          <div className="type-content">
                            <h4 className="type-label">{type.label}</h4>
                            <p className="type-description">
                              {type.description}
                            </p>
                          </div>
                          {isSelected && (
                            <div className="type-check">
                              <CheckCircle size={16} />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {formErrors.objectType && (
                    <p className="error-message">{formErrors.objectType}</p>
                  )}
                </div>

                {/* Étape 2: Détails de l'objet */}
                <div className="form-section">
                  <h3 className="section-title">
                    <span className="section-number">2</span>
                    Décrivez votre objet
                  </h3>

                  <div className="form-grid">
                    <div className="form-group full-width">
                      <label className="form-label required">
                        Nom de l'objet
                      </label>
                      <input
                        type="text"
                        name="objectName"
                        value={formData.objectName}
                        onChange={handleInputChange}
                        className={`form-input ${
                          formErrors.objectName ? "error" : ""
                        }`}
                        placeholder="Ex: iPhone 13 Pro, Portefeuille marron..."
                      />
                      {formErrors.objectName && (
                        <p className="error-message">{formErrors.objectName}</p>
                      )}
                    </div>

                    <div className="form-group full-width">
                      <label className="form-label">Description</label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Description détaillée de votre objet (couleur, marque, particularités...)"
                        rows="3"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Couleur</label>
                      <input
                        type="text"
                        name="color"
                        value={formData.color}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Ex: Noir, Argent, Bleu..."
                      />
                    </div>
                  </div>
                </div>

                {/* Étape 3: Vos coordonnées */}
                <div className="form-section">
                  <h3 className="section-title">
                    <span className="section-number">3</span>
                    Vos coordonnées
                  </h3>
                  <p className="section-description">
                    Ces informations seront utilisées pour vous contacter si
                    votre objet est retrouvé
                  </p>

                  <div className="form-grid">
                    <div className="form-group full-width">
                      <label className="form-label required">
                        <User size={16} />
                        Votre nom complet
                      </label>
                      <input
                        type="text"
                        name="ownerName"
                        value={formData.ownerName}
                        onChange={handleInputChange}
                        className={`form-input ${
                          formErrors.ownerName ? "error" : ""
                        }`}
                        placeholder="Jean Dupont"
                      />
                      {formErrors.ownerName && (
                        <p className="error-message">{formErrors.ownerName}</p>
                      )}
                    </div>

                    <div className="form-group full-width">
                      <label className="form-label">
                        <Mail size={16} />
                        Votre email <span className="optional-text">(optionnel)</span>
                      </label>
                      <input
                        type="email"
                        name="ownerEmail"
                        value={formData.ownerEmail}
                        onChange={handleInputChange}
                        className={`form-input ${
                          formErrors.ownerEmail ? "error" : ""
                        }`}
                        placeholder="jean.dupont@email.com"
                      />
                      {formErrors.ownerEmail && (
                        <p className="error-message">{formErrors.ownerEmail}</p>
                      )}
                    </div>

                    <div className="form-group full-width">
                      <label className="form-label required">
                        <Phone size={16} />
                        Téléphone principal
                      </label>
                      <input
                        type="tel"
                        name="ownerPhone"
                        value={formData.ownerPhone}
                        onChange={handleInputChange}
                        className={`form-input ${
                          formErrors.ownerPhone ? "error" : ""
                        }`}
                        placeholder="+33 6 12 34 56 78"
                      />
                      <small className="form-help">
                        Ce numéro sera utilisé pour les notifications SMS
                      </small>
                      {formErrors.ownerPhone && (
                        <p className="error-message">{formErrors.ownerPhone}</p>
                      )}
                    </div>

                    <div className="form-group full-width">
                      <label className="form-label">
                        <Phone size={16} />
                        Téléphone secondaire <span className="optional-text">(optionnel)</span>
                      </label>
                      <input
                        type="tel"
                        name="ownerPhone2"
                        value={formData.ownerPhone2}
                        onChange={handleInputChange}
                        className={`form-input ${
                          formErrors.ownerPhone2 ? "error" : ""
                        }`}
                        placeholder="+33 6 12 34 56 78"
                      />
                      <small className="form-help">
                        Numéro de téléphone secondaire
                      </small>
                      {formErrors.ownerPhone && (
                        <p className="error-message">
                          {formErrors.ownerPhone2}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Confirmation et soumission */}
                <div className="form-section">
                  <div className="confirmation-box">
                    <h3 className="confirmation-title">
                      <CheckCircle size={20} />
                      Presque terminé !
                    </h3>
                    <p className="confirmation-text">
                      En cliquant sur "Protéger mon objet", vous acceptez que
                      vos informations soient utilisées pour vous contacter en
                      cas de découverte de votre objet.
                    </p>

                    <div className="qr-preview">
                      <div className="qr-code-display">
                        <div className="qr-code-container">
                          {/* QR code visuel */}
                          <div className="qr-code-placeholder">
                            <span>QR</span>
                          </div>
                        </div>
                        <div className="qr-code-info">
                          <h4>Votre QR Code Unique</h4>
                          <p>
                            Code: <strong>{qrCode?.qrId}</strong>
                          </p>
                          <p>
                            Statut:{" "}
                            <span className="status-unassigned">
                              À assigner
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="submit-btn"
                    >
                      {submitting ? (
                        <>
                          <div className="spinner-small"></div>
                          Assignation en cours...
                        </>
                      ) : (
                        <>
                          <Save size={20} />
                          Protéger mon objet
                        </>
                      )}
                    </button>

                    <p className="privacy-notice">
                      Vos données sont protégées et ne seront partagées qu'avec
                      la personne qui trouvera votre objet. Consultez notre{" "}
                      <a href="/privacy" className="privacy-link">
                        politique de confidentialité
                      </a>
                      .
                    </p>
                  </div>
                </div>
              </form>
            </div>

            <div className="assign-sidebar">
              <div className="sidebar-card">
                <h3 className="sidebar-title">Comment ça marche ?</h3>
                <ul className="steps-list">
                  <li className="step-item">
                    <div className="step-number">1</div>
                    <div className="step-content">
                      <h4>Assignez votre objet</h4>
                      <p>
                        Remplissez ce formulaire pour lier votre objet au QR
                        code
                      </p>
                    </div>
                  </li>
                  <li className="step-item">
                    <div className="step-number">2</div>
                    <div className="step-content">
                      <h4>Collez le QR code</h4>
                      <p>Placez l'autocollant QR code sur votre objet</p>
                    </div>
                  </li>
                  <li className="step-item">
                    <div className="step-number">3</div>
                    <div className="step-content">
                      <h4>Soyez protégé</h4>
                      <p>
                        Si votre objet est perdu, le trouveur pourra vous
                        contacter
                      </p>
                    </div>
                  </li>
                  <li className="step-item">
                    <div className="step-number">4</div>
                    <div className="step-content">
                      <h4>Recevez des alertes</h4>
                      <p>
                        Vous recevrez une notification immédiate si votre objet
                        est scanné
                      </p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="sidebar-card">
                <h3 className="sidebar-title">Conseils</h3>
                <ul className="tips-list">
                  <li className="tip-item">
                    <CheckCircle size={16} />
                    <span>Placez le QR code à un endroit visible</span>
                  </li>
                  <li className="tip-item">
                    <CheckCircle size={16} />
                    <span>Prenez une photo de votre objet pour référence</span>
                  </li>
                  <li className="tip-item">
                    <CheckCircle size={16} />
                    <span>
                      Gardez une copie de vos informations d'assignation
                    </span>
                  </li>
                  <li className="tip-item">
                    <CheckCircle size={16} />
                    <span>Mettez à jour vos coordonnées si elles changent</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="assign-footer">
        <div className="container">
          <p>© 2024 UltimaTag. Tous droits réservés.</p>
          <p>
            <a href="/privacy">Politique de confidentialité</a> •
            <a href="/terms">Conditions d'utilisation</a> •
            <a href="/contact">Contact</a>
          </p>
        </div>
      </footer>
    </div>
  );
}